import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import { store } from '../../utils/store';

export function Assinatura({ adminId, email, empresa }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    carregar();
    // Checa se voltou de um pagamento bem-sucedido
    const params = new URLSearchParams(window.location.search);
    if (params.get('pagamento') === 'sucesso') {
      setMsg('✅ Pagamento confirmado! Sua assinatura está sendo ativada...');
      setTimeout(() => {
        window.history.replaceState({}, '', window.location.pathname);
        carregar();
      }, 3000);
    }
  }, [adminId]);

  const carregar = async () => {
    setLoading(true);
    const { data } = await supabase.rpc('verificar_acesso', { p_admin_id: adminId });
    setStatus(data);
    setLoading(false);
  };

  const assinar = async () => {
    setCheckoutLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('criar-checkout', {
        body: {
          admin_id: adminId,
          email,
          nome_empresa: empresa,
          return_url: window.location.origin,
        },
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      setMsg('Erro ao iniciar pagamento. Tente novamente.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <p style={{ color: '#888', fontSize: 14 }}>Verificando assinatura...</p>
      </div>
    </div>
  );

  const diasRestantes = status?.dias_trial_restantes ?? 0;
  const expirado = !status?.tem_acesso;

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Logo */}
        <div style={styles.logo}>
          <div style={styles.logoIcon}>E</div>
          <div>
            <div style={styles.logoNome}>AgendaPro</div>
            <div style={styles.logoSub}>Gestão de Agendamentos</div>
          </div>
        </div>

        {/* Mensagem de retorno */}
        {msg && <div style={styles.msgOk}>{msg}</div>}

        {/* Status */}
        {expirado ? (
          <>
            <div style={styles.badge('expirado')}>Período gratuito encerrado</div>
            <h2 style={styles.titulo}>Sua conta está bloqueada</h2>
            <p style={styles.desc}>
              Seus 15 dias gratuitos terminaram. Assine agora para continuar
              usando o AgendaPro. Todos os seus dados estão salvos e seguros.
            </p>
          </>
        ) : (
          <>
            <div style={styles.badge('trial')}>
              {diasRestantes === 1 ? '⚠️ Último dia' : `🎁 ${diasRestantes} dias grátis restantes`}
            </div>
            <h2 style={styles.titulo}>Aproveite o período gratuito</h2>
            <p style={styles.desc}>
              Você tem mais <strong>{diasRestantes} dia{diasRestantes !== 1 ? 's' : ''}</strong> de
              acesso gratuito. Assine agora e continue sem interrupções.
            </p>
          </>
        )}

        {/* Card de plano */}
        <div style={styles.plano}>
          <div style={styles.planoTopo}>
            <span style={styles.planoNome}>Plano Mensal</span>
            <span style={styles.planoBadge}>Mais popular</span>
          </div>
          <div style={styles.planoPreco}>
            <span style={styles.precoValor}>R$ 44,99</span>
            <span style={styles.precoSub}>/mês</span>
          </div>
          <ul style={styles.planoItens}>
            {[
              'Agendamentos ilimitados',
              'Múltiplos profissionais',
              'Link de agendamento online',
              'Portal do cliente',
              'Histórico completo',
              'Suporte por WhatsApp',
            ].map(item => (
              <li key={item} style={styles.planoItem}>
                <span style={styles.check}>✓</span> {item}
              </li>
            ))}
          </ul>
          <button style={styles.btnAssinar} onClick={assinar} disabled={checkoutLoading}>
            {checkoutLoading ? 'Redirecionando...' : 'Assinar agora — R$ 44,99/mês'}
          </button>
          <p style={styles.planoRodape}>
            Cancele a qualquer momento • Sem fidelidade • Pagamento seguro via Stripe
          </p>
        </div>

        {/* Continuar no trial (só se ainda tiver acesso) */}
        {!expirado && (
          <button style={styles.btnSecundario} onClick={() => window.history.back()}>
            Continuar com o período gratuito →
          </button>
        )}

        <button style={styles.btnSair} onClick={() => store.logout()}>
          Sair da conta
        </button>

      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f5f6fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
  },
  card: {
    background: '#fff',
    borderRadius: 20,
    border: '0.5px solid #e5e7eb',
    padding: '36px 32px',
    maxWidth: 460,
    width: '100%',
    boxShadow: '0 4px 24px rgba(0,0,0,.06)',
    textAlign: 'center',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    justifyContent: 'center',
    marginBottom: 28,
  },
  logoIcon: {
    width: 40, height: 40,
    background: '#2563eb',
    borderRadius: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 18, fontWeight: 700, color: '#fff',
  },
  logoNome: { fontSize: 16, fontWeight: 700, color: '#111' },
  logoSub:  { fontSize: 11, color: '#888' },
  badge: (tipo) => ({
    display: 'inline-block',
    background: tipo === 'expirado' ? '#fef2f2' : '#eff6ff',
    color:      tipo === 'expirado' ? '#dc2626'  : '#2563eb',
    border:     `0.5px solid ${tipo === 'expirado' ? '#fecaca' : '#bfdbfe'}`,
    borderRadius: 20,
    padding: '5px 14px',
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 16,
  }),
  titulo: { fontSize: 20, fontWeight: 700, color: '#111', marginBottom: 10 },
  desc: { fontSize: 14, color: '#555', lineHeight: 1.6, marginBottom: 24 },
  msgOk: {
    background: '#f0fdf4', color: '#16a34a',
    border: '0.5px solid #86efac',
    borderRadius: 10, padding: '10px 14px',
    fontSize: 13, marginBottom: 20,
  },
  plano: {
    background: '#f8faff',
    border: '1.5px solid #2563eb',
    borderRadius: 16,
    padding: '24px 20px',
    marginBottom: 16,
    textAlign: 'left',
  },
  planoTopo: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  planoNome: { fontSize: 15, fontWeight: 700, color: '#111' },
  planoBadge: {
    background: '#2563eb', color: '#fff',
    borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600,
  },
  planoPreco: { marginBottom: 16 },
  precoValor: { fontSize: 36, fontWeight: 800, color: '#111' },
  precoSub:   { fontSize: 14, color: '#888', marginLeft: 4 },
  planoItens: { listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'grid', gap: 8 },
  planoItem: { fontSize: 13, color: '#444', display: 'flex', alignItems: 'center', gap: 8 },
  check: { color: '#2563eb', fontWeight: 700, fontSize: 14 },
  btnAssinar: {
    width: '100%',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '13px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'opacity .12s',
    marginBottom: 10,
  },
  planoRodape: { fontSize: 11, color: '#aaa', textAlign: 'center', margin: 0 },
  btnSecundario: {
    background: 'none', border: 'none',
    color: '#2563eb', fontSize: 13,
    cursor: 'pointer', fontFamily: 'inherit',
    marginBottom: 10, display: 'block', width: '100%',
    padding: '8px',
  },
  btnSair: {
    background: 'none', border: 'none',
    color: '#aaa', fontSize: 12,
    cursor: 'pointer', fontFamily: 'inherit',
  },
};
